import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useRoutes } from "../../AppRoutes";
import IBAConsoleLayout from "Layout/IBAConsoleLayout";
import Toggle from "../../components/Toggle/Toggle";
import "./homePage.css";
import Button from "@amzn/awsui-components-react/polaris/button";

const ENV_PAIRS: { prod: string; beta: string }[] = [
  { prod: "https://prod.iba-console.ab.amazon.dev", beta: "https://beta.iba-console.ab.amazon.dev" },
];

const SECTIONS = [
  {
    title: "Order Management",
    description: "Manage and track orders across different fulfillment types",
    routes: ["order-info", "cfo-sfo-mappings"],
  },
  {
    title: "Business Operations",
    description: "Handle business enrollments and compliance",
    routes: ["business-enrolment", "business-unenrolment", "business-audit"],
  },
  {
    title: "Monitoring",
    description: "Monitor accounting reports and reconciliation results",
    routes: ["accounting-monitor"],
  },
];

const CARD_DESCRIPTIONS: Record<string, string> = {
  "cfo-sfo-mappings":
    "View and manage Customer Fulfilled Order (CFO) to Seller Fulfilled Order (SFO) mappings for order tracking.",
  "business-audit": "Review business information Invoice By Amazon related.",
  "business-enrolment": "Enrol new businesses in IBA with automated validation and marketplace selection.",
  "business-unenrolment": "Manage business unenrolment with proper documentation and reason tracking.",
  "order-info": "Order information viewer with comprehensive data about purchase, seller, and much more.",
  "accounting-monitor": "Browse monthly accounting reports and view reconciliation results as a sortable table.",
};

const HomePage = () => {
  const navigate = useNavigate();
  const routes = useRoutes();
  const [hoveredCard, setHoveredCard] = useState<string | null>(null);
  const currentPair = ENV_PAIRS.find(
    (pair) => window.location.href.includes(pair.prod) || window.location.href.includes(pair.beta)
  );
  const isProd = currentPair ? window.location.href.includes(currentPair.prod) : false;
  const timeOfDay = new Date().getHours() < 12 ? "morning" : new Date().getHours() < 18 ? "afternoon" : "evening";

  const changeEnvironment = (toProd: boolean) => {
    if (currentPair) {
      window.location.href = toProd ? currentPair.prod : currentPair.beta;
    }
  };

  return (
    <IBAConsoleLayout>
      <div className="home-page-container">
        <div className="hero-section env-gradient">
          <div className="hero-content">
            <div className="hero-left">
              <div className="hero-main">
                <h1>IBA Console</h1>
                <p>Good {timeOfDay}! Welcome to your central hub for IBA management</p>
              </div>
            </div>
            {currentPair && (
              <div className="environment-switcher">
                <Toggle onLabel="Beta" offLabel="Prod" onChange={changeEnvironment} isChecked={isProd} />
              </div>
            )}
          </div>
        </div>

        <div className="main-content">
          {SECTIONS.map((section) => {
            const sectionRoutes = routes.filter((route) => section.routes.includes(route.page));
            if (sectionRoutes.length === 0) {
              return null;
            }
            return (
              <div key={section.title} className="section-container">
                <div className="section-header">
                  <div className="section-title">
                    <h2>{section.title}</h2>
                    <p>{section.description}</p>
                  </div>
                </div>
                <div className="cards-grid">
                  {sectionRoutes.map((route) => (
                    <div
                      key={route.page}
                      className={`feature-card ${hoveredCard === route.page ? "hovered" : ""}`}
                      onMouseEnter={() => setHoveredCard(route.page)}
                      onMouseLeave={() => setHoveredCard(null)}
                    >
                      <h3>{route.name}</h3>
                      <p>{CARD_DESCRIPTIONS[route.page]}</p>
                      <Button variant="primary" onClick={() => navigate(`/${route.page}`)} className="feature-button">
                        Go to {route.name}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </IBAConsoleLayout>
  );
};

export default HomePage;
